import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Chip } from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { orange } from "@mui/material/colors";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import api from "../../api/axios";
import MarkMilestoneComplete from "./MarkMilestoneComplete";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

export default function OnceOfficeTemplate({ selectedProject }) {
  const [milestones, setMilestones] = React.useState([]);
  const [openMarkCompleteModal, setOpenMarkCompleteModal] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(null);

  const fetchMilestones = async () => {
    const { data: milestoneResponse } = await api.get(
      "/projects/milestones/" + selectedProject.id
    );
    setMilestones(milestoneResponse.data);
  };

  useEffect(() => {
    fetchMilestones();
  }, []);

  const onMarkComplete = (milestone) => {
    // Open Modal
    setActiveMilestone(milestone);
    setOpenMarkCompleteModal(true);
  };

  const onSeekClarification = (milestone) => {
    // Implement seek clarification logic here
    console.log("Seeking clarification for milestone:", milestone);
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Project Milestones
        </Typography>

        <Timeline position="right">
          {milestones.map((milestone, index) => {
            const isCompleted = milestone.is_complete;

            return (
              <TimelineItem key={milestone.id || index}>
                <TimelineSeparator>
                  <TimelineDot
                    color={isCompleted ? "success" : "warning"}
                    variant="outlined"
                  >
                    {isCompleted ? (
                      <CheckCircleIcon
                        sx={{ color: orange[500] }}
                        fontSize="small"
                      />
                    ) : (
                      <HourglassBottomIcon fontSize="small" />
                    )}
                  </TimelineDot>

                  {index < milestones.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ pb: 4 }}>
                  <Stack spacing={1}>
                    {/* Milestone Title */}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {milestone.milestone_name}
                    </Typography>

                    {/* Status Chip */}
                    <Chip
                      label={
                        isCompleted
                          ? "Completed " +
                            dayjs(milestone.completion_date).fromNow()
                          : "Pending"
                      }
                      size="small"
                      color={isCompleted ? "success" : "warning"}
                      sx={{ width: "fit-content" }}
                    />

                    {/* Completion Date */}
                    {milestone.completedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Completed on: <strong>{milestone.completedAt}</strong>
                      </Typography>
                    )}

                    {/* Completion Remark */}
                    {milestone.remark && (
                      <Typography
                        variant="body2"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.04)",
                          p: 1,
                          borderRadius: 1,
                        }}
                      >
                        {milestone.remark}
                      </Typography>
                    )}

                    {/* Action Buttons */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      mt={1}
                      alignItems="center"
                      sx={{
                        height: 42,
                        visibility: isCompleted ? "hidden" : "visible",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => onMarkComplete(milestone)}
                        sx={{
                          height: 42,
                          textTransform: "none",
                        }}
                      >
                        Mark Complete
                      </Button>

                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<WhatsAppIcon />}
                        onClick={() => onSeekClarification(milestone)}
                        sx={{
                          height: 42,
                          textTransform: "none",
                        }}
                      >
                        Ask Client
                      </Button>
                    </Stack>
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>

      {activeMilestone && (
        <MarkMilestoneComplete
          open={openMarkCompleteModal}
          milestone={activeMilestone}
          onClose={() => setOpenMarkCompleteModal(false)}
          selectedProject={selectedProject}
          onSuccess={fetchMilestones}
        />
      )}
    </>
  );
}
